import React, { useState, useEffect, useContext, useMemo } from 'react';
import _, { startCase } from 'lodash';
import { AuthContext } from 'contexts';
import {
  buildFullName,
  featureEnabled,
  isAuthForLocation,
  isAuthForRole,
  sortUserName,
} from 'utils';
import { ParsedSupraLockboxJsonType } from 'api/SupraLockboxController';
import { getOrgUsers, searchForMLSStaff } from 'api/UserController';

import errorReporter from '@app/errorReporter';
import { ObjectId, SupraSystemSubscriptionStatus } from '@bb/domain/dist/types';
import type { IUserOrgResponse } from '@/app-package';

import { Modal, Table, Select, SelectOption, Contact } from 'components';

import { ColumnsType } from 'antd/es/table';
import { useBreakpoint, useDebounce } from 'hooks';
import { SupraCoopType } from '@bb/domain/dist/types/SupraSystemSubscription';
import {
  isBranchAdmin,
  isGlobalAdmin,
  isOwner,
  isManager,
  isTeamAdmin,
} from '@bb/users';

type UserSelectModalProps = {
  lockbox: ParsedSupraLockboxJsonType;
  isDirty: boolean;
  setIsDirty: any;
  permittedUsers: IUserOrgResponse['users'];
  setPermittedUsers: React.Dispatch<
    React.SetStateAction<IUserOrgResponse['users']>
  >;
  openModal: any;
  setOpenModal: any;
  setOfficeWideShacklePermissionsGranted: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

const UserSelectModal: React.FC<UserSelectModalProps> = ({
  lockbox,
  isDirty,
  setIsDirty,
  permittedUsers,
  setPermittedUsers,
  openModal,
  setOpenModal,
  setOfficeWideShacklePermissionsGranted,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<IUserOrgResponse['users']>(
    [],
  );

  const [selectOptions, setSelectOptions] = useState<IUserOrgResponse['users']>(
    [],
  );
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  const { lg } = useBreakpoint();
  const { auth } = useContext(AuthContext);
  const debouncedSearchText = useDebounce(searchText, 300);

  const isMlsUser = auth?.org.type === 'mlsStaff';

  const lockboxSlowAssignmentEnhancementsEnabled = featureEnabled(
    'LockboxSlowAssignmentEnhancements',
  );

  const canAssignMlsWideGrantee = useMemo(() => {
    const authIsBranchAdmin = isBranchAdmin(auth);
    const authIsTeamAdmin = isTeamAdmin(auth);

    /**
     * Updated rules for users who can grant MLS wide shackle permissions based on BET-2314
     *
     * https://honeywell.atlassian.net/browse/BET-2314
     */
    const updatedMlsWidePermissions =
      lockboxSlowAssignmentEnhancementsEnabled &&
      // MLS users
      (isMlsUser ||
        // Global Admin, Owner, and Manager can assign MLS wide permissions when owner belongs to their org and they are not the assignee.
        ((isGlobalAdmin(auth) || isOwner(auth) || isManager(auth)) &&
          lockbox.parsedOwner?.orgId === auth?.org.id &&
          !(
            lockbox.assigneeType === 'user' && lockbox.assignee === auth?.id
          )) ||
        /**
         *Branch Admins can assign MLS wide permissions if owner is within their org
         * AND owner location is within their authorized locations. Assignee cannot be themselves.
         */
        (authIsBranchAdmin &&
          lockbox.parsedOwner?.orgId === auth?.org.id &&
          isAuthForLocation(auth, {
            location:
              lockbox.ownerType === 'office'
                ? lockbox.ownerBranchIndex
                : lockbox.parsedOwner?.location,
            locations: [],
          }) &&
          !(
            lockbox.assigneeType === 'user' && lockbox.assignee === auth?.id
          )) ||
        (authIsTeamAdmin &&
          ((lockbox.parsedOwner.type === 'user' &&
            isAuthForRole({
              auth,
              org: lockbox.parsedOwner?.orgId,
              teams: [...(lockbox.parsedOwner?.teams || [])],
            })) ||
            ((lockbox.parsedOwner.type !== 'office' ||
              lockbox.parsedOwner?.orgId === auth?.org.id) &&
              lockbox.parsedAssignee.type === 'user' &&
              isAuthForRole({
                auth,
                org: lockbox.parsedAssignee?.orgId,
                teams: [...(lockbox.parsedAssignee?.teams || [])],
              }))) &&
          !(
            lockbox.assigneeType === 'user' && lockbox.assignee === auth?.id
          )) ||
        // Agents who are the owner can assign MLS wide permissions
        (lockbox.ownerType === 'user' && lockbox.owner === auth?.id) ||
        // When owner is user and auth user is within the assignee's org, they can assign MLS wide permissions
        (lockbox.ownerType === 'user' &&
          lockbox.assigneeType === 'user' &&
          lockbox.parsedAssignee?.orgId === auth?.org.id) ||
        // When owner is MLS/BOR, assignee user/office can assign MLS wide permissions
        ((lockbox.ownerType === 'mls' || lockbox.ownerType === 'board') &&
          (lockbox.assigneeType === 'user' ||
            lockbox.assigneeType === 'office')));

    return lockboxSlowAssignmentEnhancementsEnabled
      ? updatedMlsWidePermissions
      : lockbox.ownerType === 'user' ||
          ((lockbox.ownerType === 'mls' || lockbox.ownerType === 'board') &&
            (lockbox.assigneeType === 'org' ||
              lockbox.assigneeType === 'branch' ||
              lockbox.assigneeType === 'user'));
  }, [lockbox, isMlsUser, lockboxSlowAssignmentEnhancementsEnabled, auth]);

  // Team Admin can only assign team-wide grantees if owner is an office outside of their org
  const canOnlyAssignTeamWideGrantee = useMemo(
    () =>
      lockboxSlowAssignmentEnhancementsEnabled &&
      isTeamAdmin(auth) &&
      lockbox.ownerType === 'office' &&
      lockbox.parsedOwner?.orgId !== auth?.org.id &&
      lockbox.assigneeType === 'user' &&
      lockbox.parsedAssignee?.orgId === auth?.org.id &&
      isAuthForRole({
        auth,
        org: lockbox.parsedAssignee?.orgId,
        teams: [...(lockbox.parsedAssignee?.teams || [])],
      }),
    [lockboxSlowAssignmentEnhancementsEnabled, lockbox, auth],
  );

  // An agent who is the lockbox assignee can only assign office wide grantees.
  const canOnlyAssignOfficeWideGrantee = useMemo(
    () =>
      lockboxSlowAssignmentEnhancementsEnabled &&
      !canOnlyAssignTeamWideGrantee &&
      lockbox.ownerType === 'office' &&
      lockbox.assigneeType === 'user' &&
      lockbox.parsedAssignee?.orgId === auth?.org.id,
    [
      lockboxSlowAssignmentEnhancementsEnabled,
      canOnlyAssignTeamWideGrantee,
      lockbox,
      auth?.org.id,
    ],
  );

  const selectUserColumns: ColumnsType<IUserOrgResponse['users'][0]> = [
    {
      title: 'NAME',
      dataIndex: 'firstName',
      key: 'name',
      render: (name, { firstName, lastName }) =>
        buildFullName(firstName, lastName),
    },
    {
      title: 'TYPE',
      dataIndex: 'role',
      key: 'role',
      render: role => startCase(role.replace('_', ' ').toLocaleLowerCase()),
    },
    {
      title: 'MEMBER #',
      dataIndex: 'mls_numbers',
      key: 'mls_numbers',
      render: mls_numbers =>
        mls_numbers && mls_numbers.length
          ? mls_numbers
              ?.map(({ board, number }: todo) => `${board}: ${number}`)
              ?.join(', ')
          : '',
    },
    isMlsUser
      ? {
          title: 'BRANCH',
          dataIndex: 'branchName',
          key: 'branchName',
          render: branchName => branchName,
        }
      : {
          title: 'BRANCH',
          dataIndex: 'location',
          key: 'location',
          render: (loc, { location, address }) =>
            auth?.org?.locations?.[Number(location)]?.name,
        },
  ];

  const getUsersToAssign = async (userNameToSearch: string, size?: number) => {
    const supraSubscriptionStatuses = [
      SupraSystemSubscriptionStatus.subscribed,
    ];
    const excludeUsers = [
      ...permittedUsers.map(user => user.id),
      ...(lockbox.owner && lockbox.ownerType === 'user' ? [lockbox.owner] : []),
      ...(lockbox.assignee && lockbox.assigneeType === 'user'
        ? [lockbox.assignee]
        : []),
    ];
    let response;
    let isMlsSearchResponse = false;

    if (isMlsUser) {
      const getOrgId = () => {
        let orgId;

        if (!canAssignMlsWideGrantee) {
          if (lockbox.assignee && lockbox.assigneeType === 'org') {
            orgId = lockbox.assignee;
          }
          if (lockbox.assignee && lockbox.assigneeType !== 'org') {
            orgId = lockbox.parsedAssignee?.orgId;
          }
          if (!lockbox.assignee && lockbox.ownerType! === 'org') {
            orgId = lockbox.owner;
          }
          if (!lockbox.assignee && lockbox.ownerType !== 'org') {
            orgId = lockbox.parsedOwner?.orgId;
          }
        }
        return orgId;
      };

      response = await searchForMLSStaff({
        name: userNameToSearch,
        supraSubscriptionStatuses,
        eligibleGranteesOnly: true,
        excludeUsers,
        orgId: getOrgId(),
        size,
      });
      isMlsSearchResponse = true;
    } else {
      response = await getOrgUsers({
        userName: userNameToSearch,
        supraSubscriptionStatuses,
        eligibleGranteesOnly: true,
        excludeUsers,
        isCrossOrgSearch: canAssignMlsWideGrantee,
        size,
        isShacklePermissionRequest: true,
      });
    }

    const users =
      lockboxSlowAssignmentEnhancementsEnabled && !isMlsSearchResponse
        ? (response as IUserOrgResponse)?.users
            .filter(user => {
              // For mls wide grantee assignments, allow searched users including coops.
              if (canAssignMlsWideGrantee) {
                return true;
              }

              if (canOnlyAssignTeamWideGrantee) {
                return (
                  user.orgId === auth?.org.id &&
                  user.teams?.some((teamId: ObjectId) =>
                    lockbox.parsedAssignee?.teams?.includes(teamId.toString()),
                  )
                );
              }

              // For office wide grantee assignments, select users only within assignee user's office.
              if (canOnlyAssignOfficeWideGrantee) {
                return (
                  user.orgId === auth?.org.id &&
                  user.location === lockbox.parsedAssignee?.location &&
                  user.address === lockbox.parsedAssignee?.address
                );
              }
              return false;
            })
            .map(user => ({ ...user })) || []
        : response?.users.map(user => ({ ...user })) || [];

    return users as IUserOrgResponse['users'];
  };

  useEffect(() => {
    (async () => {
      let usersWithOrg: IUserOrgResponse['users'] = [];
      try {
        setSearchLoading(true);
        // soft limit to fetch 5000 subscribed users
        usersWithOrg = await getUsersToAssign(debouncedSearchText, 5000);
      } catch (err) {
        errorReporter.error(err);
      } finally {
        setSearchLoading(false);
      }

      setSelectOptions(usersWithOrg.sort(sortUserName));
    })();
  }, [debouncedSearchText, permittedUsers]);

  const optionsMap: Record<string, IUserOrgResponse['users'][0]> =
    React.useMemo(() => {
      if (!selectOptions?.length) return {};
      const result: typeof optionsMap = {};
      selectOptions.forEach(user => {
        result[user.id] = user;
      });
      return result;
    }, [selectOptions]);

  return (
    <Modal
      title={'Add Users'}
      open={openModal}
      okText={'Add'}
      width={lg ? '50%' : '90%'}
      onOk={() => {
        setPermittedUsers(users =>
          _.unionBy(users, selectedUsers, 'id').sort(sortUserName),
        );
        setSelectedUsers([]);
        /**
         * Check for dirty state here (i.e shackles not saved), and avoid resetting officeWideShacklePermissions as permissions
         * may not have been saved from previous shackle permission table screen. For example, user maybe have granted office
         * wide permissions, but before saving they try to add individual users.
         */
        if (!isDirty) {
          setOfficeWideShacklePermissionsGranted(false);
        }
        setIsDirty(true);
        setOpenModal(false);
      }}
      okButtonProps={{ disabled: selectedUsers.length === 0 }}
      onCancel={() => {
        setSelectedUsers([]);
        setOpenModal(false);
      }}
      maskClosable={false}
    >
      {canAssignMlsWideGrantee
        ? `All users within the MLS/BOR are available${lockboxSlowAssignmentEnhancementsEnabled ? ', including Co-op users' : ''}.`
        : canOnlyAssignOfficeWideGrantee
          ? `Only users within the assignee's office are available.`
          : canOnlyAssignTeamWideGrantee
            ? `Only users within the assignee's team are available.`
            : 'Only users within the brokerage are available.'}
      <Select
        value={selectedUsers
          .map(user => ({
            key: user.id,
            value: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            label: buildFullName(user.firstName, user.lastName),
          }))
          .sort(sortUserName)}
        onSearch={text => setSearchText(text)}
        onSelect={userId => {
          setSelectedUsers(users =>
            [...users, optionsMap[userId]].sort(sortUserName),
          );
          setSearchText('');
        }}
        onDeselect={userId =>
          setSelectedUsers(users => users.filter(user => user.id !== userId))
        }
        mode="multiple"
        optionLabelProp="label"
        filterOption={false}
        placeholder="&#x1F50D;&#xFE0E; Search"
        loading={searchLoading}
      >
        {selectOptions.map(user => (
          <SelectOption
            key={user.id}
            value={user.id}
            label={buildFullName(user.firstName, user.lastName)}
          >
            <Contact
              contact={user}
              avatarSize={32}
              showBranchName
              showMlsIds
              disableClick
              // @ts-ignore add coopType to IUserOrgResponse[users]
              {...(user.coopType &&
                // @ts-ignore add coopType to IUserOrgResponse[users]
                user.coopType !== SupraCoopType.PRIMARY && {
                  showCoopType: true,
                })}
            />
          </SelectOption>
        ))}
      </Select>
      {selectedUsers.length > 0 && (
        <Table
          columns={selectUserColumns}
          dataSource={selectedUsers}
          rowKey={(user: { id: string }) => user.id}
          style={{ paddingTop: 10 }}
        />
      )}
    </Modal>
  );
};

export default UserSelectModal;
